import { ensureFreshToken, keycloak } from "@/lib/keycloak";

/**
 * API client — thin wrapper around fetch that:
 *  - Resolves the base URL from VITE_API_BASE_URL
 *  - Attaches a fresh Keycloak access token (forces refresh if ≤60s validity)
 *  - Parses the doc 22 §6 error envelope into a typed ApiError
 *  - Converts 401 into a silent login redirect (keeps the UX coherent when a
 *    refresh token has expired mid-session)
 *
 * Not pre-binding Tanstack Query here — this stays a plain fetch helper so
 * it's usable from Query hooks, action handlers, and server-side scripts.
 */

const BASE = import.meta.env.VITE_API_BASE_URL || "";
const GET_RETRY_DELAYS_MS = [400, 1_200] as const;
const RETRYABLE_GET_STATUSES = new Set([502, 503, 504]);

export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    correlationId?: string;
    fields?: Record<string, string>;
  };
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly correlationId?: string;
  readonly fields?: Record<string, string>;

  constructor(status: number, body: ApiErrorBody) {
    super(body.error.message);
    this.name = "ApiError";
    this.status = status;
    this.code = body.error.code;
    this.correlationId = body.error.correlationId;
    this.fields = body.error.fields;
  }
}

interface ApiRequestInit extends Omit<RequestInit, "body"> {
  body?: unknown;
  /** Skip auth header attachment — for public endpoints. */
  skipAuth?: boolean;
  /** Query string params (values coerced to strings, undefined omitted). */
  params?: Record<string, string | number | boolean | undefined | null>;
  /** Idempotency-Key header (doc 22 §8). Client-generated ULID preferred. */
  idempotencyKey?: string;
  /** Opt into a successful non-JSON response, such as a CSV export. */
  responseType?: "json" | "text";
}

function buildUrl(path: string, params?: ApiRequestInit["params"]): string {
  const rawUrl = path.startsWith("http") ? path : BASE ? `${BASE}${path}` : path;
  const url = new URL(rawUrl, window.location.origin);
  if (params) {
    for (const [key, val] of Object.entries(params)) {
      if (val === undefined || val === null) continue;
      url.searchParams.set(key, String(val));
    }
  }
  return url.toString();
}

export async function apiRequest<T = unknown>(
  path: string,
  init: ApiRequestInit = {},
): Promise<T> {
  const {
    body,
    skipAuth,
    params,
    idempotencyKey,
    responseType = "json",
    headers,
    ...rest
  } = init;

  const reqHeaders = new Headers(headers);
  if (body !== undefined && !reqHeaders.has("Content-Type")) {
    reqHeaders.set("Content-Type", "application/json");
  }
  if (!reqHeaders.has("Accept")) {
    reqHeaders.set("Accept", responseType === "text" ? "text/csv" : "application/json");
  }
  if (idempotencyKey) {
    reqHeaders.set("Idempotency-Key", idempotencyKey);
  }
  if (!skipAuth && keycloak.authenticated) {
    const token = await ensureFreshToken(60);
    if (token) reqHeaders.set("Authorization", `Bearer ${token}`);
  }

  const request = {
    ...rest,
    headers: reqHeaders,
    body: body === undefined ? undefined : JSON.stringify(body),
  };
  const method = (request.method ?? "GET").toUpperCase();
  let res: Response;

  for (let attempt = 0; ; attempt += 1) {
    try {
      res = await fetch(buildUrl(path, params), request);
    } catch (error) {
      const shouldRetry =
        method === "GET" &&
        error instanceof TypeError &&
        attempt < GET_RETRY_DELAYS_MS.length;

      if (!shouldRetry) throw error;
      await waitForRetry(GET_RETRY_DELAYS_MS[attempt] ?? 0);
      continue;
    }

    const shouldRetry =
      method === "GET" &&
      RETRYABLE_GET_STATUSES.has(res.status) &&
      attempt < GET_RETRY_DELAYS_MS.length;

    if (!shouldRetry) break;

    void res.body?.cancel().catch(() => undefined);
    await waitForRetry(GET_RETRY_DELAYS_MS[attempt] ?? 0);
  }

  // 204 No Content
  if (res.status === 204) return undefined as T;

  const contentType = res.headers.get("content-type") ?? "";
  const isJson = contentType.includes("application/json");
  const payload = isJson ? await res.json().catch(() => null) : await res.text();

  if (res.ok && responseType === "text") {
    return payload as T;
  }

  if (res.ok && !isJson) {
    throw new ApiError(502, {
      error: {
        code: "common.invalid_api_response",
        message: "Expected JSON from PulseBoard API but received a non-JSON response.",
      },
    });
  }

  if (!res.ok) {
    if (res.status === 401 && !skipAuth) {
      // Refresh token also expired — bounce to login. Don't throw an error
      // the UI would render; just navigate away.
      void keycloak.login({ redirectUri: window.location.href });
    }
    const errBody = normaliseErrorBody(payload, res.status);
    throw new ApiError(res.status, errBody);
  }

  return payload as T;
}

function normaliseErrorBody(payload: unknown, status: number): ApiErrorBody {
  if (payload && typeof payload === "object" && "error" in payload) {
    const rawError = (payload as { error?: unknown }).error;
    if (rawError && typeof rawError === "object") {
      const value = rawError as Partial<ApiErrorBody["error"]>;
      if (typeof value.code === "string") {
        return {
          error: {
            code: value.code,
            message:
              typeof value.message === "string" && value.message
                ? value.message
                : value.code,
            correlationId:
              typeof value.correlationId === "string" ? value.correlationId : undefined,
            fields:
              value.fields && typeof value.fields === "object" ? value.fields : undefined,
          },
        };
      }
    }

    // Phase C endpoints intentionally use the literal {"error":"code"}
    // shape. Preserve that code while still presenting a useful Error.
    if (typeof rawError === "string") {
      const detail = (payload as { detail?: unknown }).detail;
      return {
        error: {
          code: rawError,
          message: typeof detail === "string" && detail ? detail : rawError,
        },
      };
    }
  }

  return {
    error: {
      code: "common.unknown_error",
      message:
        typeof payload === "string"
          ? payload.slice(0, 240)
          : `Request failed with status ${status}`,
    },
  };
}

function waitForRetry(delayMs: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, delayMs));
}

/** Convenience helpers — thin shortcuts for the common verbs. */
export const api = {
  get: <T>(path: string, init?: Omit<ApiRequestInit, "method" | "body">) =>
    apiRequest<T>(path, { ...init, method: "GET" }),
  getText: (path: string, init?: Omit<ApiRequestInit, "method" | "body" | "responseType">) =>
    apiRequest<string>(path, { ...init, method: "GET", responseType: "text" }),
  post: <T>(
    path: string,
    body?: unknown,
    init?: Omit<ApiRequestInit, "method">,
  ) => apiRequest<T>(path, { ...init, method: "POST", body }),
  patch: <T>(
    path: string,
    body?: unknown,
    init?: Omit<ApiRequestInit, "method">,
  ) => apiRequest<T>(path, { ...init, method: "PATCH", body }),
  put: <T>(
    path: string,
    body?: unknown,
    init?: Omit<ApiRequestInit, "method">,
  ) => apiRequest<T>(path, { ...init, method: "PUT", body }),
  delete: <T>(path: string, init?: Omit<ApiRequestInit, "method" | "body">) =>
    apiRequest<T>(path, { ...init, method: "DELETE" }),
};
